import React, { useState } from 'react';
import * as yup from 'yup';

import SidePanelContentSection from '../../../common2/side-panel/SidePanelContentSection';
import { ClaimContext, ClaimFileType, ClaimType, OnChangeClaimContext } from '../types';
import { ClaimStepContainerProps } from './ClaimStepContainer';
import { useValidateStep } from './hooks/useValidateStep';
import WeightInput from './WeightInput';
import { Input } from '../../../common2/Input/Input';
import ColumnsLayout from '../../../common/layout/ColumnsLayout';
import useOnFormChange from '../../../../hooks/useOnFormChange';
import FileImportMessage from './FileImportMessage';
import { ImportFileState } from './useFileImport';
import LoadingStep from './LoadingStep';
import Uploader from '../../../common/Uploader';
import InputHelpLink from '../InputHelpLink';
import Column from '../../../common/layout/Column';
import ItemPicturesUploader from './ItemPicturesUploader';
import TextArea from '../../../common2/TextArea';
import { computeAdditionalInfoRequirements } from '../claimsHelpers';
import './yupClaimsExtensions';
import { useClaimsShopSettings } from '../../ClaimsShopSettingsContext';

export type ClaimContextForAdditionalInfoStep = Pick<
  ClaimContext,
  | 'submissionInfo'
  | 'weight'
  | 'recipientPhoneNumber'
  | 'declarationOfRecipient'
  | 'lmcCaseId'
  | 'carrierDamageReports'
  | 'type'
  | 'damageInfo'
  | 'isDelivered'
  | 'proofOfConsumerPayment'
  | 'deliveryDate'
  | 'postalReturnDeliveryDate'
  | 'picturesOfDamage'
  | 'picturesOfMissingItem'
  | 'shopSettings'
  | 'claimRequirements'
  | 'dataProcessingConsent'
>;

export type AdditionalInfoStepProps = {
  claim: ClaimContextForAdditionalInfoStep;
  as: React.ComponentType<ClaimStepContainerProps>;
  onChange: OnChangeClaimContext;
  importDORState: ImportFileState;
  importConsentState: ImportFileState;
} & ClaimStepContainerProps;

const schema = yup.object({
  weight: yup
    .number()
    .draftablePositive()
    .empty('')
    .label('Weight')
    .when('$isWeightRequired', {
      is: true,
      then: (schema) => schema.draftableRequired(),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),
  recipientPhoneNumber: yup
    .string()
    .label('Recipient phone number')
    .when('$isRecipientPhoneNumberRequired', {
      is: true,
      then: (schema) => schema.draftableRequired(),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),
  declarationOfRecipient: yup
    .array()
    .when('$isDORRequired', {
      is: true,
      then: (schema) => schema.draftableMinOneAsRequired().max(1),
    })
    .label('Declaration of recipient'),
  carrierDamageReports: yup
    .array()
    .when('$isCarrierDamageReportRequired', {
      is: true,
      then: (schema) => schema.draftableMinOneAsRequired(),
    })
    .label('Carrier damage report'),
  lmcCaseId: yup
    .string()
    .max(64)
    .label('Carrier case ID')
    .when('$isCarrierCaseIdRequired', {
      is: true,
      then: (schema) => schema.draftableRequired(),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),
  picturesOfDamage: yup
    .array()
    .when('$isDamagePhotosRequired', {
      is: true,
      then: (schema) => schema.draftableMinOneAsRequired(),
    })
    .label('Pictures of damage'),
  damageInfo: yup
    .string()
    .label('Damage info')
    .when('$isDamageInfoRequired', {
      is: true,
      then: (schema) => schema.draftableRequired(),
      otherwise: (schema) => schema.nullable(),
    }),
  picturesOfMissingItem: yup
    .array()
    .when('$isPicturesOfMissingItemRequired', {
      is: true,
      then: (schema) => schema.draftableMinOneAsRequired(),
    })
    .label('Pictures of missing item'),
  proofOfConsumerPayment: yup.array().max(1).label('Proof of consumer payment'),
  dataProcessingConsent: yup
    .array()
    .when('$isDataProcessingConsentRequired', {
      is: true,
      then: (schema) => schema.draftableMinOneAsRequired().max(1),
    })
    .label('Data processing consent'),
});

function AdditionalInfoStep(props: AdditionalInfoStepProps) {
  const { claim, onChange, onNext, as: Container, importDORState, importConsentState, ...restProps } = props;

  const shopSettings = useClaimsShopSettings();
  const { recipientPhoneNumber, weight, carrierDamageReports, lmcCaseId, damageInfo, type } = claim;
  const onInputChange = useOnFormChange(onChange);
  const [isNextButtonDisabled, setIsNextButtonDisabled] = useState(false);

  const isProofOfConsumerPaymentUploaderShown = type === ClaimType.CASH_ON_DELIVERY;

  const {
    weightRequirement,
    recipientPhoneNumberRequirement,
    carrierCaseIdRequirement,
    carrierDamageReportRequirement,
    picturesOfMissingItemRequirement,
    damagePhotosRequirement,
    damageDescriptionRequirement,
    declarationOfRecipientRequirement,
    dataProcessingConsentRequirement,
    isCarrierSpecificDataRequired,
  } = computeAdditionalInfoRequirements({ ...claim, shopSettings });

  const isDamageInfoShown = damageDescriptionRequirement.isShown || damagePhotosRequirement.isShown;

  const validationContext = {
    isWeightRequired: weightRequirement.isRequired,
    isRecipientPhoneNumberRequired: recipientPhoneNumberRequirement.isRequired,
    isDORRequired: declarationOfRecipientRequirement.isRequired,
    isCarrierCaseIdRequired: carrierCaseIdRequirement.isRequired,
    isCarrierDamageReportRequired: carrierDamageReportRequirement.isRequired,
    isDamageInfoRequired: damageDescriptionRequirement.isRequired,
    isDamagePhotosRequired: damagePhotosRequirement.isRequired,
    isPicturesOfMissingItemRequired: picturesOfMissingItemRequirement.isRequired,
    isDataProcessingConsentRequired: dataProcessingConsentRequirement.isRequired,
  };

  const { onClickSaveDraft, onClickNext, onClickBack, errors } = useValidateStep(
    schema,
    claim,
    validationContext,
    props
  );

  if (declarationOfRecipientRequirement.isShown && importDORState === 'searching') {
    return <LoadingStep {...props} text="CLAIMS_IMPORT_DOR_SEARCHING" />;
  }

  if (dataProcessingConsentRequirement.isShown && importConsentState === 'searching') {
    return <LoadingStep {...props} text="CLAIMS_IMPORT_CONSENT_SEARCHING" />;
  }

  return (
    <Container
      {...restProps}
      onSaveDraft={onClickSaveDraft}
      onNext={onClickNext}
      onBack={onClickBack}
      isDisabled={isNextButtonDisabled}
    >
      {declarationOfRecipientRequirement.isShown && (
        <FileImportMessage state={importDORState} fileType={ClaimFileType.DECLARATION_OF_RECIPIENT} />
      )}
      {dataProcessingConsentRequirement.isShown && (
        <FileImportMessage state={importConsentState} fileType={ClaimFileType.DATA_PROCESSING_CONSENT} />
      )}
      <Column>
        {isDamageInfoShown && (
          <SidePanelContentSection heading="DAMAGE_CLAIM_SECTION_HEADER">
            <Column>
              <ItemPicturesUploader
                name="picturesOfDamage"
                label="Pictures of damage"
                isRequired={damagePhotosRequirement.isRequired}
                helpText="CLAIMS_DAMAGE_PICTURES_HELP_TEXT"
                claim={claim}
                errors={errors}
                onChange={onInputChange}
                onBusy={setIsNextButtonDisabled}
                uploaderHelpLinkElement={
                  // eslint-disable-next-line max-len
                  <InputHelpLink to="https://support.portal.sevensenders.com/support/solutions/articles/15000048062-submission-document-requirements#Damage-Photo-Requirements%C2%A0">
                    Damage photo requirements
                  </InputHelpLink>
                }
              />
              <TextArea
                label="DAMAGE_DESC"
                value={damageInfo}
                onChange={onInputChange}
                name="damageInfo"
                helpText="DAMAGE_TEXT_AREA_HELP"
                error={errors.damageInfo}
                isRequired={damageDescriptionRequirement.isRequired}
                noErrorTranslation
              />
            </Column>
          </SidePanelContentSection>
        )}
        {isProofOfConsumerPaymentUploaderShown && (
          <Uploader
            name="proofOfConsumerPayment"
            label="CLAIMS_DOCUMENTS_UPLOAD_POCP"
            topRightElement={
              // eslint-disable-next-line max-len
              <InputHelpLink to="https://support.portal.sevensenders.com/support/solutions/articles/15000048062-submission-document-requirements">
                Proof of consumer payment requirements
              </InputHelpLink>
            }
            files={claim.proofOfConsumerPayment}
            errorWithTranslation={errors.proofOfConsumerPayment}
            allowedFileTypes={['application/pdf', 'image/png', 'image/jpg', 'image/jpeg']}
            helpText="CLAIMS_DOCUMENTS_UPLOAD_POCP_HELP_TEXT"
            onChange={onInputChange}
            onBusy={setIsNextButtonDisabled}
            maxNumberOfFiles={1}
          />
        )}
        {picturesOfMissingItemRequirement.isShown && (
          <SidePanelContentSection heading="MISSING_ITEM_SECTION_HEADER">
            <ItemPicturesUploader
              name="picturesOfMissingItem"
              label="MISSING_ITEM_PICTURES_UPLOADER_LABEL"
              helpText="CLAIMS_DAMAGE_PICTURES_HELP_TEXT"
              claim={claim}
              errors={errors}
              onChange={onInputChange}
              onBusy={setIsNextButtonDisabled}
              isRequired={picturesOfMissingItemRequirement.isRequired}
            />
          </SidePanelContentSection>
        )}
        {declarationOfRecipientRequirement.isShown && (
          <SidePanelContentSection heading="DOR_SECTION_HEADER">
            <Uploader
              name="declarationOfRecipient"
              label="CLAIMS_DOCUMENTS_UPLOAD_DOR"
              topRightElement={
                // eslint-disable-next-line max-len
                <InputHelpLink to="https://support.portal.sevensenders.com/support/solutions/articles/15000048062-submission-document-requirements#Declaration-of-Receipt-(DOR)-Requirements%C2%A0">
                  DOR requirements
                </InputHelpLink>
              }
              isRequired={declarationOfRecipientRequirement.isRequired}
              files={claim.declarationOfRecipient}
              errorWithTranslation={errors.declarationOfRecipient}
              allowedFileTypes={['application/pdf', 'image/png', 'image/jpg', 'image/jpeg']}
              helpText="CLAIMS_DOCUMENTS_UPLOAD_DOR_HELP_TEXT"
              onChange={onInputChange}
              onBusy={setIsNextButtonDisabled}
              maxNumberOfFiles={1}
            />
          </SidePanelContentSection>
        )}
        {isCarrierSpecificDataRequired && (
          <SidePanelContentSection heading="CARRIER_SPECIFIC_REQUIREMENTS">
            <ColumnsLayout>
              {weightRequirement.isShown && (
                <WeightInput
                  name="weight"
                  label="Weight"
                  value={weight}
                  error={errors.weight}
                  onChange={onInputChange}
                  noErrorTranslation
                  isRequired={weightRequirement.isRequired}
                />
              )}
              {recipientPhoneNumberRequirement.isShown && (
                <Input
                  name="recipientPhoneNumber"
                  label="Recipient phone number"
                  placeholder="Add recipient phone number"
                  isRequired={recipientPhoneNumberRequirement.isRequired}
                  value={recipientPhoneNumber}
                  error={errors.recipientPhoneNumber}
                  onChange={onInputChange}
                  noErrorTranslation
                />
              )}
            </ColumnsLayout>
            {dataProcessingConsentRequirement.isShown && (
              <Uploader
                name="dataProcessingConsent"
                label="CLAIMS_DOCUMENTS_UPLOAD_DATA_PROCESSING_CONSENT"
                isRequired={dataProcessingConsentRequirement.isRequired}
                files={claim.dataProcessingConsent}
                errorWithTranslation={errors.dataProcessingConsent}
                helpText="CLAIMS_DOCUMENTS_UPLOAD_DATA_PROCESSING_CONSENT_HELP"
                allowedFileTypes={['application/pdf', 'image/png', 'image/jpg', 'image/jpeg']}
                onChange={onInputChange}
                onBusy={setIsNextButtonDisabled}
                maxNumberOfFiles={1}
              />
            )}
            {(carrierCaseIdRequirement.isShown || carrierDamageReportRequirement.isShown) && (
              <Column>
                {carrierCaseIdRequirement.isShown && (
                  <ColumnsLayout>
                    <Input
                      isRequired={carrierCaseIdRequirement.isRequired}
                      name="lmcCaseId"
                      value={lmcCaseId}
                      label="CARRIER_CASE_ID_LABEL"
                      placeholder="Add carrier case ID"
                      error={errors.lmcCaseId}
                      onChange={onInputChange}
                    />
                  </ColumnsLayout>
                )}
                {carrierDamageReportRequirement.isShown && (
                  <Uploader
                    name="carrierDamageReports"
                    files={carrierDamageReports}
                    label="Carrier damage report"
                    isRequired={carrierDamageReportRequirement.isRequired}
                    errorWithTranslation={errors.carrierDamageReports}
                    allowedFileTypes={['application/pdf', 'image/jpg', 'image/jpeg', 'image/png']}
                    helpText="CLAIMS_DOCUMENTS_UPLOAD_CARRIER_DAMAGE_REPORT_HELP"
                    onChange={onInputChange}
                    onBusy={setIsNextButtonDisabled}
                    maxNumberOfFiles={10}
                  />
                )}
              </Column>
            )}
          </SidePanelContentSection>
        )}
      </Column>
    </Container>
  );
}

export default React.memo(AdditionalInfoStep);
